import { DISCOVERY_SEED, THEME_ORDER } from '../data/discoverySeed.js';
import { t } from '../i18n/index.js';

/**
 * Render the cold-start Explore grid into `container`.
 * Shows themed cards grouped by THEME_ORDER; each card fires `onSelect(trackId)`.
 * Hides the container when `onSelect` is called (caller clears it via hideExploreGrid).
 *
 * No network calls are made by this function. Cards only trigger lookups on click.
 *
 * @param {HTMLElement} container
 * @param {(trackId: string) => void} onSelect  called when user picks a card
 */
export function renderExploreGrid(container, onSelect) {
  if (!container) return;

  const grouped = /** @type {Map<string, typeof DISCOVERY_SEED>} */ (new Map());
  for (const theme of THEME_ORDER) grouped.set(theme, []);
  for (const entry of DISCOVERY_SEED) {
    grouped.get(entry.theme)?.push(entry);
  }

  const themeLabels = {
    global: () => t('explore.theme.global'),
    regional: () => t('explore.theme.regional'),
    insight: () => t('explore.theme.insight'),
  };

  let html = `<div class="explore-grid" role="region" aria-label="${escHtml(t('explore.title'))}">`;
  html += `<h2 class="explore-grid__title">${escHtml(t('explore.title'))}</h2>`;
  html += `<p class="explore-grid__subtitle">${escHtml(t('explore.subtitle'))}</p>`;

  for (const theme of THEME_ORDER) {
    const entries = grouped.get(theme) ?? [];
    if (entries.length === 0) continue;
    html += `<section class="explore-theme" aria-label="${escHtml(themeLabels[theme]())}">`;
    html += `<h3 class="explore-theme__label">${escHtml(themeLabels[theme]())}</h3>`;
    html += `<ul class="explore-theme__cards" role="list">`;
    for (const entry of entries) {
      const ariaLabel = t('explore.cardAriaLabel', { name: entry.name });
      html += `<li class="explore-card-wrap">`;
      html += `<button
        class="explore-card"
        type="button"
        data-track-id="${escHtml(entry.trackId)}"
        aria-label="${escHtml(ariaLabel)}"
      >`;
      html += `<span class="explore-card__name">${escHtml(entry.name)}</span>`;
      html += `<span class="explore-card__blurb">${escHtml(entry.blurb)}</span>`;
      html += `</button></li>`;
    }
    html += `</ul></section>`;
  }

  html += `</div>`;
  container.innerHTML = html;
  container.hidden = false;

  container.querySelectorAll('.explore-card').forEach((btn) => {
    btn.addEventListener('click', () => {
      const trackId = /** @type {HTMLElement} */ (btn).dataset.trackId;
      if (trackId) onSelect(trackId);
    });
  });
}

/**
 * Hide and clear the explore grid container.
 * @param {HTMLElement|null} container
 */
export function hideExploreGrid(container) {
  if (!container) return;
  container.hidden = true;
  container.innerHTML = '';
}

/**
 * Minimal HTML escape for user-facing strings inserted via innerHTML.
 * @param {string} str
 * @returns {string}
 */
function escHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
